// @ts-check
// _common.js — shared primitives used by more than one settings section.
//
// Extracted from settings-panel.js (Stage C of the monolith split). These
// helpers were module-private in the host and called from several sections
// (workspaces, encryption, calendars). Sections import the ones they need; the
// host keeps using them via aliased imports until workspaces moves out too.

import { confirmStatus, promptStatus, updateStatus } from '../../ui/status-pill.js';

// A single circular native <input type="color"> styled to match the static
// dots elsewhere in the panel. Two phases:
//   - 'input'  fires continuously while the native picker is open; callers
//              should treat that as live preview only — no Settings.set, no
//              re-render, just visual update.
//   - 'change' fires once when the picker closes; that's where the commit
//              (persist + re-render) happens.
// Splitting the two prevents a re-render from detaching this <input> mid-drag,
// which would close the native colour dialog under the user.
/**
 * @param {string | null} value - the initial colour (defaults to #000000).
 * @param {(value: string, phase: 'preview' | 'commit') => void} onChange
 * @param {string} [aria] - an aria-label.
 * @returns {HTMLInputElement}
 */
export function colorPicker(value: any, onChange: any, aria: any) {
  const picker = document.createElement('input');
  picker.type = 'color';
  picker.value = value || '#000000';
  picker.className = 'settings-color-input';
  if (aria) picker.setAttribute('aria-label', aria);
  picker.addEventListener('input', () => onChange(picker.value, 'preview'));
  picker.addEventListener('change', () => onChange(picker.value, 'commit'));
  return picker;
}

// A full-width primary call-to-action in a `settings-form` wrapper (the CSS
// stretches it to the row width) — "＋ New workspace", "＋ Add new calendar",
// "＋ Pair new device". Pass `disabled` to show it inert (the pairing CTA while
// a session is live).
export function addCta(
  label: string,
  onClick: () => void,
  opts: { disabled?: boolean } = {}
): HTMLElement {
  const form = document.createElement('div');
  form.className = 'settings-form';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--primary';
  btn.textContent = label;
  btn.disabled = !!opts.disabled;
  if (!opts.disabled) btn.addEventListener('click', onClick);
  form.appendChild(btn);
  return form;
}

// A heading's right-hand "value button" — "Add calendar", "Add filter", "Pair
// device", "Sign in". Builds the styled button only; the caller supplies the
// full click body (each section's expand-and-act differs).
function valueButton(
  label: string,
  onClick: (e: Event) => void,
  opts: { title?: string } = {}
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'settings-value-btn';
  btn.textContent = label;
  if (opts.title) btn.title = opts.title;
  btn.addEventListener('click', onClick);
  return btn;
}

// A group heading's right-hand "expand" CTA — a value button that uncollapses
// the group, optionally runs `before`, then re-renders the section. Shared by
// every section whose heading offers a direct action (Encryption pair/decrypt,
// Calendars add, Filters add, Sync sign-in).
//
// `before` is AWAITED before `invalidate()`, so an async step that must land
// before the re-render (e.g. Filters persisting a new blank row) works by
// returning its promise; a fire-and-forget follow-up (e.g. starting the pairing
// ceremony, focusing a field on the next frame) just returns void and the
// re-render fires immediately. `stopPropagation` defaults true — set it false to
// also let the row's own activation run (Sync's sign-in does this).
export function expandHeadingCta(
  ctx: { openExpansions: { add: (id: string) => void }; invalidate: () => void },
  groupId: string,
  label: string,
  opts: { title: string; stopPropagation?: boolean; before?: () => void | Promise<void> }
): HTMLButtonElement {
  return valueButton(
    label,
    async (e) => {
      if (opts.stopPropagation !== false) e.stopPropagation(); // don't also toggle the row
      ctx.openExpansions.add(groupId);
      if (opts.before) await opts.before();
      ctx.invalidate();
    },
    { title: opts.title }
  );
}

// Reflect an icon button's reveal state — shared by the workspace members icon
// and the calendar-list reveal icon. Toggles `.is-open` + aria-expanded and sets
// the aria-label from the caller's open/closed strings.
export function syncRevealIcon(
  btn: HTMLElement,
  open: boolean,
  openLabel: string,
  closedLabel: string
): void {
  btn.classList.toggle('is-open', open);
  btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  btn.setAttribute('aria-label', open ? openLabel : closedLabel);
}

// Build a reveal-toggle icon button — the shared shape behind the workspace
// members icon and the calendar-list reveal icon. Makes a
// `btn btn--icon btn--ghost <cls>` button, sets its glyph (`svg`) + static hover
// `title`, seeds the open/closed reveal state via syncRevealIcon, and wires the
// stop-propagation → toggle(id) → invalidate click handler. The per-re-render
// aria-label patch stays with each caller's own sync helper (its open/closed
// strings are dynamic, driven off updateControl), matching how syncRevealIcon is
// already shared for exactly this button family.
export function buildRevealToggle(
  ctx: { openExpansions: Set<string>; invalidate: () => void },
  id: string,
  opts: { svg: string; cls: string; title: string; openLabel: string; closedLabel: string }
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `btn btn--icon btn--ghost ${opts.cls}`;
  btn.innerHTML = opts.svg;
  btn.title = opts.title;
  syncRevealIcon(btn, ctx.openExpansions.has(id), opts.openLabel, opts.closedLabel);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (ctx.openExpansions.has(id)) ctx.openExpansions.delete(id);
    else ctx.openExpansions.add(id);
    ctx.invalidate();
  });
  return btn;
}

/**
 * @param {string} text
 * @returns {HTMLParagraphElement} a settings help-text paragraph.
 */
export function makeP(text: any) {
  const p = document.createElement('p');
  p.className = 'settings-helptext';
  p.textContent = text;
  return p;
}

/**
 * @param {string} msg
 * @param {string} [kind]
 * @returns {void}
 */
export function toast(msg: any, kind: any) {
  if (typeof updateStatus === 'function') updateStatus(msg, { kind: kind || 'info' });
}

/**
 * Short text entry routed through the status pill (promptStatus) — the inline,
 * non-blocking replacement for the appPrompt() modal. Used for backup
 * passphrase entry.
 * @param {any} opts - string, or { message?, title?, password?, placeholder?, confirmLabel? }
 * @returns {Promise<string | null>}
 */
export async function prompt(opts: any) {
  if (typeof opts === 'string') opts = { message: opts };
  const label = opts.message || opts.title || 'Enter a value';
  return promptStatus(label, {
    type: opts.password ? 'password' : 'text',
    placeholder: opts.placeholder,
    confirmLabel: opts.confirmLabel,
  });
}

/**
 * Low-stakes settings confirms route through the status pill (P13), not a
 * blocking modal. Flattens { title, message } into one concise pill question;
 * danger → the error tint.
 * @param {any} opts - string, or { message?, title?, danger?, confirmLabel?, cancelLabel? }
 * @returns {Promise<boolean>}
 */
export async function confirm(opts: any) {
  if (typeof opts === 'string') opts = { message: opts };
  const msg = [opts.title, opts.message].filter(Boolean).join(' ') || 'Are you sure?';
  return confirmStatus(msg, {
    kind: opts.danger ? 'error' : 'warning',
    confirmLabel: opts.confirmLabel,
    cancelLabel: opts.cancelLabel,
  });
}

/**
 * The always-present (hidden-when-empty) inline error div used by the retained
 * settings forms (sync / encryption / calendars). role=alert so a screen reader
 * announces it when it becomes visible. Pair with {@link setErrorDiv} to patch.
 * @param {string} [text]
 * @returns {HTMLDivElement}
 */
export function errorDiv(text = ''): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'settings-error';
  el.setAttribute('role', 'alert');
  el.textContent = text;
  el.hidden = !text;
  return el;
}

/**
 * Patch an existing {@link errorDiv} in place: set its text and hide it when
 * empty. No-op if the element is missing.
 * @param {Element | null} el
 * @param {string} [text]
 * @returns {void}
 */
export function setErrorDiv(el: Element | null, text = ''): void {
  if (!el) return;
  (el as HTMLElement).textContent = text;
  (el as HTMLElement).hidden = !text;
}

/**
 * The standard destructive "×" icon-button used across settings section rows
 * (disconnect / remove / revoke). Uniform class + glyph; per-site aria-label,
 * optional title/disabled/extra-class, and click handler. (The workspace-row
 * delete button keeps its own build — it carries extra state branching.)
 * @param {{ ariaLabel: string; title?: string; disabled?: boolean; className?: string; onClick?: (e: MouseEvent) => void }} opts
 * @returns {HTMLButtonElement}
 */
export function removeButton(opts: {
  ariaLabel: string;
  title?: string;
  disabled?: boolean;
  className?: string;
  onClick?: (e: MouseEvent) => void;
}): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `btn btn--icon btn--ghost btn--danger${opts.className ? ` ${opts.className}` : ''}`;
  btn.textContent = '×';
  btn.setAttribute('aria-label', opts.ariaLabel);
  if (opts.title) btn.title = opts.title;
  if (opts.disabled) btn.disabled = true;
  if (opts.onClick) btn.addEventListener('click', opts.onClick);
  return btn;
}
